/*
 * EmployeeCodigoEtica — lista somente leitura da biblioteca de Código
 * de Ética e Conduta. O funcionário visualiza cada PDF separadamente.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { API, useAuth } from "../App";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import {
  ShieldCheck, FileText, Eye, Download, Loader2, FolderOpen,
  CalendarClock, CheckCircle2, PenLine,
} from "lucide-react";
import SignReceiptDialog from "./SignReceiptDialog";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("pt-BR", {
      day: "2-digit", month: "2-digit", year: "numeric",
    });
  } catch { return iso; }
};

const fmtSize = (b) => {
  if (!b) return "";
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

export default function EmployeeCodigoEtica() {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [receiptsByCodigo, setReceiptsByCodigo] = useState({}); // codigo_id → {id, status, signature_id}
  const [signOpen, setSignOpen] = useState(false);
  const [signItem, setSignItem] = useState(null);

  const loadReceipts = useCallback(async () => {
    if (!token) return;
    try {
      const r = await axios.get(`${API}/employee/document-receipts`, {
        params: { token }, timeout: 20000,
      });
      const map = {};
      (r.data?.items || []).forEach((it) => {
        if (it.source_kind === "codigo_etica" && it.source_id) {
          map[it.source_id] = {
            id: it.id,
            status: it.suspended && it.status === "pending" ? "suspended" : it.status,
            signature_id: it.signature_id,
          };
        }
      });
      setReceiptsByCodigo(map);
    } catch {
      /* silent — receipts are optional metadata */
    }
  }, [token]);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/employee/codigo-etica/list`, {
        params: { token }, timeout: 20000,
      });
      setItems(r.data?.items || []);
      loadReceipts();
    } catch {
      toast.error("Não foi possível carregar os documentos.");
    } finally { setLoading(false); }
  }, [token, loadReceipts]);

  useEffect(() => { load(); }, [load]);

  const open = async (it, download = false) => {
    setBusy(it.id + (download ? "-dl" : "-view"));
    try {
      const r = await axios.get(
        `${API}/employee/codigo-etica/${it.id}/pdf`,
        { params: { token }, responseType: "blob", timeout: 60000 },
      );
      const url = URL.createObjectURL(r.data);
      if (download) {
        const a = document.createElement("a");
        a.href = url;
        a.download = it.filename || `${it.title}.pdf`;
        document.body.appendChild(a); a.click();
        document.body.removeChild(a);
      } else {
        window.open(url, "_blank", "noopener,noreferrer");
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error("Falha ao abrir o documento.");
    } finally { setBusy(null); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16"
            data-testid="codigo-etica-loading">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                   className="text-center py-16"
                   data-testid="codigo-etica-empty">
        <FolderOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <p className="text-slate-500 text-sm mb-1">
          Nenhum documento publicado ainda.
        </p>
        <p className="text-slate-400 text-xs">
          Quando o RH publicar um Código de Ética ou Conduta, ele aparecerá aqui.
        </p>
      </motion.div>
    );
  }

  return (
    <div className="space-y-3" data-testid="codigo-etica-employee-list">
      {/* Ordenar: pendentes primeiro, depois assinados, depois sem recibo */}
      {(() => {
        const pendingCount = items.filter(
          (it) => receiptsByCodigo[it.id]?.status === "pending",
        ).length;
        if (pendingCount === 0) return null;
        return (
          <motion.div
            initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
            className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-300 rounded-xl shadow-sm"
            data-testid="codigo-etica-pending-banner"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center flex-shrink-0 animate-pulse">
                <PenLine className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-bold text-amber-900">
                  {pendingCount === 1
                    ? "Você tem 1 documento pendente de assinatura"
                    : `Você tem ${pendingCount} documentos pendentes de assinatura`}
                </p>
                <p className="text-xs text-amber-800 mt-0.5">
                  Assine agora para confirmar que leu e está ciente do conteúdo.
                </p>
              </div>
            </div>
          </motion.div>
        );
      })()}

      <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
        <p className="text-xs text-emerald-900">
          <ShieldCheck className="w-4 h-4 inline-block mr-1 mb-0.5" />
          Documentos oficiais da AVI. Cada arquivo é independente — abra
          somente o que deseja ler.
        </p>
      </div>

      {[...items]
        .sort((a, b) => {
          const rank = (id) => {
            const s = receiptsByCodigo[id]?.status;
            if (s === "pending") return 0;
            if (!s) return 1;
            return 2; // signed
          };
          return rank(a.id) - rank(b.id);
        })
        .map((it, idx) => {
        const isViewBusy = busy === it.id + "-view";
        const isDlBusy = busy === it.id + "-dl";
        const receiptStatus = receiptsByCodigo[it.id]?.status;
        const isPending = receiptStatus === "pending";
        return (
          <motion.div
            key={it.id}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.03 }}
            data-testid={`codigo-etica-employee-item-${it.id}`}
          >
            <Card
              className={`hover:shadow-md transition-shadow ${
                isPending
                  ? "border-2 border-amber-400 bg-amber-50/40 shadow-md"
                  : "border-slate-200"
              }`}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className={`flex-shrink-0 w-11 h-11 rounded-lg flex items-center justify-center ${
                    isPending ? "bg-amber-200" : "bg-emerald-100"
                  }`}>
                    <FileText className={`w-5 h-5 ${
                      isPending ? "text-amber-800" : "text-emerald-700"
                    }`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-slate-800">
                        {it.title}
                      </p>
                      {isPending && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-amber-500 text-white px-2 py-0.5 rounded-full uppercase tracking-wide">
                          <PenLine className="w-3 h-3" />
                          Aguardando assinatura
                        </span>
                      )}
                    </div>
                    {it.description && (
                      <p className="text-xs text-slate-600 mt-1">
                        {it.description}
                      </p>
                    )}
                    <div className="mt-1.5 flex items-center gap-3 text-[11px] text-slate-500">
                      <span className="flex items-center gap-1">
                        <CalendarClock className="w-3 h-3" />
                        {fmtDate(it.created_at)}
                      </span>
                      {it.size_bytes ? (
                        <span className="font-mono">{fmtSize(it.size_bytes)}</span>
                      ) : null}
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2 flex-shrink-0">
                    <Button size="sm" variant="outline"
                             onClick={() => open(it, false)}
                             disabled={!!busy}
                             data-testid={`codigo-etica-employee-view-${it.id}`}>
                      {isViewBusy
                        ? <Loader2 className="w-4 h-4 animate-spin" />
                        : <><Eye className="w-4 h-4 sm:mr-1.5" />
                            <span className="hidden sm:inline">Ler</span></>}
                    </Button>
                    <Button size="sm"
                             onClick={() => open(it, true)}
                             disabled={!!busy}
                             data-testid={`codigo-etica-employee-download-${it.id}`}
                             className="bg-emerald-600 hover:bg-emerald-700 text-white">
                      {isDlBusy
                        ? <Loader2 className="w-4 h-4 animate-spin" />
                        : <><Download className="w-4 h-4 sm:mr-1.5" />
                            <span className="hidden sm:inline">Baixar</span></>}
                    </Button>
                    {receiptStatus === "signed" ? (
                      <Button size="sm" variant="outline"
                               disabled
                               data-testid={`codigo-etica-signed-${it.id}`}
                               className="border-emerald-300 text-emerald-700 bg-emerald-50 cursor-default">
                        <CheckCircle2 className="w-4 h-4 sm:mr-1.5" />
                        <span className="hidden sm:inline">Assinado</span>
                      </Button>
                    ) : isPending ? (
                      <Button size="sm"
                               onClick={() => {
                                 setSignItem(it);
                                 setSignOpen(true);
                               }}
                               disabled={!!busy}
                               data-testid={`codigo-etica-sign-${it.id}`}
                               className="bg-amber-500 hover:bg-amber-600 text-white font-bold shadow-md ring-2 ring-amber-300 animate-pulse hover:animate-none">
                        <PenLine className="w-4 h-4 sm:mr-1.5" />
                        <span>Assinar</span>
                      </Button>
                    ) : null}
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        );
      })}

      <SignReceiptDialog
        open={signOpen}
        onOpenChange={(v) => {
          setSignOpen(v);
          if (!v) { setSignItem(null); loadReceipts(); }
        }}
        sourceKind="codigo_etica"
        sourceId={signItem?.id}
        documentTitle={signItem?.title}
        onSigned={() => { loadReceipts(); }}
      />
    </div>
  );
}
